import React, { useEffect } from 'react';
import { Clock, MapPin, Shield, Thermometer, Car, Trophy, Sparkles, Heart, AlertCircle, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Home = () => {
  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "CasinoOrGamblingHall",
      "name": "Funny Point Spielhalle",
      "image": [
        "https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/vt2kqe7i_IMG_7713.jpeg",
        "https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/h4y30skp_IMG_6755.jpeg",
        "https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/h46k7bpp_IMG_6746.jpeg"
      ],
      "priceRange": "$$",
      "telephone": "+49-8231-605939",
      "email": "info@funnypoint.de",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Hunnenstraße 6",
        "addressLocality": "Königsbrunn",
        "postalCode": "86343",
        "addressRegion": "Bayern",
        "addressCountry": "DE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 48.2667,
        "longitude": 10.8893
      },
      "url": "https://funnypoint-koenigsbrunn.de",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "03:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Sunday",
          "opens": "11:00",
          "closes": "03:00"
        }
      ],
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": "Klimaanlage",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Parkplatz",
          "value": true
        }
      ],
      "description": "Funny Point - 2 Spielhallen unter einem Dach in Königsbrunn. Insgesamt 16 moderne Geldspielgeräte, neueste Software, klimatisierte Räume und großer Parkplatz. Vollständig lizenziert und jugendschutzkonform nach bayerischem Recht.",
      "keywords": "2 Spielhallen Königsbrunn, Spielhalle Königsbrunn, Geldspielgeräte, Spielautomaten, Casino Königsbrunn, Spielothek Augsburg, Doppel-Spielhalle"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);
  const images = {
    heroNight: 'https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/vt2kqe7i_IMG_7713.jpeg',
    interior1: 'https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/h4y30skp_IMG_6755.jpeg',
    interior2: 'https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/h46k7bpp_IMG_6746.jpeg',
    exteriorDay: 'https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/ghp2e6qv_IMG_6753.jpeg',
    interior3: 'https://customer-assets.emergentagent.com/job_fun-arcade-33/artifacts/dhon1yp8_IMG_2176.jpeg'
  };

  const features = [
    {
      icon: Sparkles,
      title: '2 Spielhallen - 1 Standort',
      description: 'Einzigartig: 2 vollständig ausgestattete Spielhallen unter einem Dach für maximalen Spielspaß'
    },
    {
      icon: Trophy,
      title: '16 Geldspielgeräte',
      description: 'Modernste Spielautomaten von Merkur und Novoline mit attraktiven Gewinnmöglichkeiten'
    },
    {
      icon: Sparkles,
      title: 'Neueste Software',
      description: 'Aktuellste Spiele und Software auf dem neuesten Stand der Technik'
    },
    {
      icon: Thermometer,
      title: 'Klimatisierte Räume',
      description: 'Angenehmes Spielerlebnis bei jeder Jahreszeit in modernen, klimatisierten Räumlichkeiten'
    },
    {
      icon: Shield,
      title: 'Gesetzeskonform',
      description: 'Vollständig lizenziert und jugendschutzkonform nach bayerischem Recht und Spielverordnung'
    },
    {
      icon: Car,
      title: 'Großer Parkplatz',
      description: 'Kostenlose Parkplätze direkt vor den Spielhallen in Königsbrunn'
    },
    {
      icon: Clock,
      title: 'Lange Öffnungszeiten',
      description: 'Mo-Sa 9:00-3:00 Uhr, So 11:00-3:00 Uhr - Täglich für Sie geöffnet'
    }
  ];

  const faqs = [
    {
      question: 'Was macht Funny Point besonders?',
      answer: 'Funny Point ist einzigartig in der Region Königsbrunn/Augsburg: Wir bieten 2 vollständig ausgestattete Spielhallen unter einem Dach! Das bedeutet mehr Auswahl, mehr Spielgeräte und mehr Möglichkeiten für Sie an einem zentralen Standort.'
    },
    {
      question: 'Wo befindet sich die Funny Point Spielhalle?',
      answer: 'Wir befinden uns in der Hunnenstraße 6, 86343 Königsbrunn bei Augsburg. Die beiden Spielhallen sind zentral gelegen mit sehr guter Erreichbarkeit und einem großen kostenlosen Parkplatz direkt vor der Tür.'
    },
    {
      question: 'Welche Öffnungszeiten hat Funny Point?',
      answer: 'Montag bis Samstag: 09:00 - 03:00 Uhr, Sonntag: 11:00 - 03:00 Uhr. Beide Spielhallen haben die gleichen Öffnungszeiten - wir haben fast durchgehend für Sie geöffnet!'
    },
    {
      question: 'Wie viele Spielautomaten gibt es bei Funny Point?',
      answer: 'In unseren 2 Spielhallen bieten wir Ihnen insgesamt 16 moderne Geldspielgeräte mit neuester Software von führenden Herstellern wie Merkur und Novoline. Alle Geräte sind stets auf dem aktuellsten Stand der Technik.'
    },
    {
      question: 'Ist die Spielhalle lizenziert und gesetzeskonform?',
      answer: 'Ja, absolut. Beide Funny Point Spielhallen sind vollständig lizenziert und erfüllen alle gesetzlichen Anforderungen nach bayerischem Recht. Wir sind jugendschutzkonform und halten uns strikt an alle Spielverordnungen.'
    },
    {
      question: 'Gibt es Parkmöglichkeiten?',
      answer: 'Ja! Direkt vor unseren Spielhallen steht Ihnen ein großer, kostenloser Parkplatz zur Verfügung. Sie müssen sich keine Gedanken über Parkplatzsuche machen.'
    },
    {
      question: 'Ist die Spielhalle klimatisiert?',
      answer: 'Ja, beide Spielhallen sind vollständig klimatisiert. Sie genießen das ganze Jahr über ein angenehmes Spielerlebnis bei optimaler Temperatur.'
    }
  ];

  const galleryImages = [
    images.interior1,
    images.interior2,
    images.interior3,
    images.heroNight,
    images.exteriorDay
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">FP</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Funny Point</h1>
                <p className="text-xs text-slate-400">Spielhalle</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-slate-300 hover:text-white transition-colors">Home</a>
              <a href="#about" className="text-slate-300 hover:text-white transition-colors">Über uns</a>
              <a href="#gallery" className="text-slate-300 hover:text-white transition-colors">Galerie</a>
              <a href="#hours" className="text-slate-300 hover:text-white transition-colors">Öffnungszeiten</a>
              <a href="#contact" className="text-slate-300 hover:text-white transition-colors">Kontakt</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={images.heroNight} 
            alt="Funny Point Spielhalle bei Nacht" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/80 to-slate-950"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl pt-20 pb-32">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/20 border border-red-600/50 rounded-full">
              <span className="text-red-400 text-sm font-medium">Willkommen in Königsbrunn</span>
            </div>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Funny Point<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-blue-500">
                Spielhalle
              </span>
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Erleben Sie Spielspaß auf höchstem Niveau in Königsbrunn bei Augsburg. <span className="text-red-400 font-semibold">2 Spielhallen unter einem Dach!</span> Insgesamt 16 moderne Geldspielgeräte, 
              neueste Software, klimatisierte Räume und großer Parkplatz. Täglich geöffnet - Ihre Spielhalle in Bayern.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
                onClick={scrollToContact}
              >
                Besuchen Sie uns
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg"
                onClick={() => document.getElementById('hours')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Öffnungszeiten
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Über Funny Point - 2 Spielhallen an einem Standort
            </h2>
            <p className="text-xl text-slate-300 leading-relaxed">
              Funny Point ist Ihre moderne Spielhalle im Herzen von Königsbrunn bei Augsburg in Bayern. 
              <span className="text-red-400 font-semibold"> Einzigartig in der Region: Wir bieten Ihnen 2 vollständig ausgestattete Spielhallen unter einem Dach!</span> 
              Erleben Sie ein erstklassiges Spielerlebnis in klimatisierten Räumen 
              mit den neuesten Spielautomaten von Merkur und Novoline. Unsere Spielhallen sind vollständig 
              lizenziert, gesetzeskonform und jugendschutzkonform nach bayerischem Recht und der Spielverordnung.
              Mit insgesamt 16 Geldspielgeräten, kostenlosem Parkplatz und langen Öffnungszeiten sind wir die erste Adresse 
              für Automatenspieler in Königsbrunn und Umgebung.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all duration-300 hover:transform hover:scale-105">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Unsere Spielhalle
            </h2>
            <p className="text-xl text-slate-300">
              Entdecken Sie unsere moderne und einladende Atmosphäre
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {galleryImages.map((image, index) => {
              const altTexts = [
                'Funny Point Spielhalle Innenraum mit modernen Spielautomaten und LED-Beleuchtung',
                'Geldspielgeräte mit Neon-Beleuchtung in klimatisierten Räumen',
                'Moderne Spielautomaten von Merkur und Novoline in Königsbrunn',
                'Funny Point Spielhalle Außenansicht bei Nacht - Hunnenstraße 6',
                'Funny Point Spielhalle Eingang - Königsbrunn bei Augsburg'
              ];
              return (
                <div key={index} className="group relative overflow-hidden rounded-xl aspect-square">
                  <img 
                    src={image} 
                    alt={altTexts[index]}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section id="hours" className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-blue-600 rounded-full mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Öffnungszeiten
              </h2>
              <p className="text-xl text-slate-300">
                Wir freuen uns auf Ihren Besuch
              </p>
            </div>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-4 border-b border-slate-700">
                    <span className="text-lg text-slate-300 font-medium">Montag - Samstag</span>
                    <span className="text-xl text-white font-bold">09:00 - 03:00 Uhr</span>
                  </div>
                  <div className="flex justify-between items-center py-4">
                    <span className="text-lg text-slate-300 font-medium">Sonntag</span>
                    <span className="text-xl text-white font-bold">11:00 - 03:00 Uhr</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-blue-600 rounded-full mb-4">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Besuchen Sie uns
              </h2>
              <p className="text-xl text-slate-300">
                Wir sind für Sie da
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Kontakt</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Adresse</p>
                      <p className="text-white text-lg font-medium">Hunnenstraße 6</p>
                      <p className="text-white text-lg font-medium">86343 Königsbrunn</p>
                    </div>
                    <div className="pt-4 border-t border-slate-700">
                      <p className="text-slate-400 text-sm mb-1">Telefon</p>
                      <a href="tel:+4982316059399" className="text-white text-lg font-medium hover:text-red-400 transition-colors flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        08231 605939
                      </a>
                    </div>
                    <div className="pt-4 border-t border-slate-700">
                      <p className="text-slate-400 text-sm mb-1">E-Mail</p>
                      <a href="mailto:info@funnypoint.de" className="text-white text-lg font-medium hover:text-red-400 transition-colors flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        info@funnypoint.de
                      </a>
                    </div>
                    <div className="pt-4 border-t border-slate-700">
                      <p className="text-slate-400 text-sm mb-1">Parken</p>
                      <p className="text-white">Großer kostenloser Parkplatz direkt vor der Spielhalle</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Standort</h3>
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2649.7!2d10.8893!3d48.2667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479ea1e0c5c5c5c5%3A0x0!2sHunnenstra%C3%9Fe%206%2C%2086343%20K%C3%B6nigsbrunn!5e0!3m2!1sde!2sde!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Funny Point Standort"
                    ></iframe>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Häufig gestellte Fragen
              </h2>
              <p className="text-xl text-slate-300">
                Alles, was Sie über Funny Point wissen möchten
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-slate-800/50 border-slate-700 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-white text-left hover:text-red-400 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 text-base leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Responsible Gaming Section */}
      <section className="py-16 bg-slate-950 border-t border-slate-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center">
                  <Heart className="w-8 h-8 text-red-400" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Verantwortungsvolles Spielen</h3>
                <p className="text-slate-300 mb-4 leading-relaxed">
                  Bei Funny Point steht verantwortungsvolles Spielen an erster Stelle. Wir halten uns strikt an alle gesetzlichen Vorgaben zum Spielerschutz und Jugendschutz nach bayerischem Recht.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-white font-semibold mb-1">Jugendschutz</h4>
                          <p className="text-sm text-slate-400">Kein Zutritt für Personen unter 21 Jahren (Bayern)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-white font-semibold mb-1">Spielsuchtprävention</h4>
                          <p className="text-sm text-slate-400">Informationen zur Spielsucht-Hotline verfügbar</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">FP</span>
                  </div>
                  <div>
                    <p className="text-white font-bold">Funny Point</p>
                    <p className="text-sm text-slate-400">Spielhalle</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm">
                  2 moderne Spielhallen unter einem Dach in Königsbrunn mit insgesamt 16 Geldspielgeräten, neuester Software und klimatisierten Räumen.
                </p>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">Kontakt</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <p>Hunnenstraße 6</p>
                  <p>86343 Königsbrunn</p>
                  <p>Bayern, Deutschland</p>
                  <a href="tel:+4982316059399" className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors pt-2">
                    <Phone className="w-4 h-4" />
                    08231 605939
                  </a>
                  <a href="mailto:info@funnypoint.de" className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors">
                    <Mail className="w-4 h-4" />
                    info@funnypoint.de
                  </a>
                </div>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">Öffnungszeiten</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <p>Mo - Sa: 09:00 - 03:00 Uhr</p>
                  <p>Sonntag: 11:00 - 03:00 Uhr</p>
                  <p className="pt-2 text-red-400">Täglich geöffnet!</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-slate-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-slate-400 text-sm text-center md:text-left">
                  © 2025 Funny Point Spielhalle. Alle Rechte vorbehalten.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
                  <span>Gesetzeskonform</span>
                  <span>·</span>
                  <span>Jugendschutzkonform</span>
                  <span>·</span>
                  <span>Lizenziert nach bayerischem Recht</span>
                </div>
              </div>
              <p className="text-slate-500 text-xs text-center mt-4">
                Spielhalle Königsbrunn | Geldspielgeräte Bayern | Casino Augsburg | Spielautomaten Königsbrunn | Spielothek in der Nähe
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;